import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const TermsOfService = () => {
  return (
    <>
      <SEO
        title="Terms of Service - ANIMAC Entertainment Hub"
        description="ANIMAC Terms of Service - Terms and conditions governing the use of our platform."
        url="/terms"
        type="website"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&q=80" 
              alt="Terms of Service" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/80 to-gray-900/90" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-azonix font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Terms of Service
              </h1>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Please read these terms carefully before using ANIMAC services.
              </p>
              <p className="text-gray-500 text-sm mt-4">Last updated: March 2025</p>
            </motion.div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-12 md:py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="prose prose-invert prose-lg max-w-none"
            >
              <div className="space-y-8">
                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">1. Acceptance of Terms</h2>
                  <p className="text-gray-300 leading-relaxed">
                    By accessing and using ANIMAC, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by these terms, please do not use this service.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">2. Description of Service</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    ANIMAC provides entertainment content, including:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Anime, manga, and entertainment news</li>
                    <li>Movie and TV show reviews</li>
                    <li>Community forums and discussions</li>
                    <li>Newsletter and email subscriptions</li>
                    <li>User-generated content and comments</li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">3. User Accounts</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    To access certain features, you may need to create an account. You agree to:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your account</li>
                    <li>Not share your account credentials</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized use</li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">4. User Conduct</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    You agree not to use the service to:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>Violate any laws or regulations</li>
                    <li>Infringe on intellectual property rights</li>
                    <li>Harass, abuse, or threaten others</li>
                    <li>Distribute spam or malicious content</li>
                    <li>Attempt to gain unauthorized access</li>
                    <li>Interfere with the proper working of the service</li>
                  </ul>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">5. Content and Intellectual Property</h2>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    All content on ANIMAC is protected by copyright. You may:
                  </p>
                  <ul className="list-disc list-inside text-gray-300 mt-4 space-y-2">
                    <li>View content for personal, non-commercial use</li>
                    <li>Share content with proper attribution</li>
                    <li>Submit your own original content</li>
                  </ul>
                  <p className="text-gray-400 text-sm mt-4">
                    You retain ownership of content you submit but grant ANIMAC a license to use it.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">6. Disclaimer of Warranties</h2>
                  <p className="text-gray-300 leading-relaxed">
                    ANIMAC is provided "as is" without any warranties, express or implied. We do not guarantee the accuracy, completeness, or reliability of any content.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">7. Limitation of Liability</h2>
                  <p className="text-gray-300 leading-relaxed">
                    ANIMAC shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">8. Privacy Policy</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Your privacy is important to us. Please review our <a href="/privacy" className="text-purple-400 hover:text-purple-300">Privacy Policy</a> which describes how we collect and use your information.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">9. Third-Party Links</h2>
                  <p className="text-gray-300 leading-relaxed">
                    Our service may contain links to third-party websites. We are not responsible for the content or practices of these external sites.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">10. Termination</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We reserve the right to terminate or suspend your account and access to the service at any time, with or without cause, and without notice.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">11. Changes to Terms</h2>
                  <p className="text-gray-300 leading-relaxed">
                    We may modify these terms at any time. Continued use of ANIMAC after changes constitutes acceptance of the new terms.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">12. Governing Law</h2>
                  <p className="text-gray-300 leading-relaxed">
                    These terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be resolved in the appropriate courts.
                  </p>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/10">
                  <h2 className="text-2xl font-azonix font-bold text-white mb-4">13. Contact Information</h2>
                  <p className="text-gray-300 leading-relaxed">
                    If you have questions about these Terms of Service, please contact us at iconanimac@gmail.com.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TermsOfService;